import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useMyPendingFeedbackApi, useFeedbackFormApi } from './config';
import FillFeedbackModal from './FillFeedbackModal';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import { parseTableParams } from '../../utils/Utils';

// Task 28 — bekleyen 360° formlarım: GET v1/feedback-responses/my-pending
// (Task 28 BE eklentisi — Task 12 kapanışında not edilen boşluk) satırları
// tıklandığında FillFeedbackModal açılır (form soruları GET .../form,
// gönderim POST .../fill — ikisi de Task 12/28).
//
// Müşteri geri bildirimi (2026-10-28) — "amaç ne, kullanımı nasıl belli değil":
// sayfanın ne listelediği tablonun üstünde tek satırla anlatılır; formu açmak
// için ⋮ menüsü ZORUNLU değil, satırın kendisine tıklamak yeter (OptionsMenu
// tıklamayı satıra taşımaz). Gelen Kutum kartı ?responseId= ile gelir: form,
// tablonun o anki sayfasında olmasa da kimliğiyle yüklenir (modal kendi
// GET .../my/{id}/form çağrısını yapar — sahiplik + pencere kapısı sunucuda);
// modal kapanınca parametre adresten silinir.
const Container = styled.div``;

const PageHint = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export default function FillFeedbackPage() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const { data, fetching, pagination, refresh } = useMyPendingFeedbackApi(queryParams);
  const { getForm, submitFill } = useFeedbackFormApi();
  const { values: { responseId: linkedResponseId }, clear: clearDeepLink } = useDeepLinkParams(['responseId']);

  const [modalResponseId, setModalResponseId] = useState(null);

  useEffect(() => {
    if (linkedResponseId) setModalResponseId(linkedResponseId);
  }, [linkedResponseId]);

  const openFill = useCallback((row) => setModalResponseId(row.id), []);
  const closeFill = useCallback(() => {
    setModalResponseId(null);
    clearDeepLink();
  }, [clearDeepLink]);
  const handleFilled = useCallback(() => {
    closeFill();
    refresh();
  }, [closeFill, refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.feedback360.fields.employee'),
      options: { filter: false, sort: false },
    },
    {
      name: 'evaluatorRole',
      label: t('performance.feedback360.fields.role'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`performance.feedback360.role.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'periodName',
      label: t('performance.feedback360.fields.period'),
      options: { filter: false, sort: false },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [{
            type: 'edit',
            label: t('performance.feedback360.fillForm.action'),
            onClick: () => openFill(row),
          }];
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [t, data, openFill]);

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, columns));
  }, [columns]);

  return (
    <Container>
      <FillFeedbackModal
        open={!!modalResponseId}
        responseId={modalResponseId}
        onClose={closeFill}
        onFilled={handleFilled}
        getForm={getForm}
        submitFill={submitFill}
      />

      <PageHint data-hint="fill-feedback">{t('performance.feedback360.fillForm.hint')}</PageHint>

      <DataTable
        title={t('performance.feedback360.fillForm.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
        onRowClick={openFill}
      />
    </Container>
  );
}
